import { useParams, Link } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import { Seo } from "@/lib/seo";
import { Loading } from "@/components/States";
import { DetailError } from "@/components/DetailError";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { EventCard } from "@/components/Cards";
import { CTAButton } from "@/components/CTAButton";
import { onImgError } from "@/lib/images";
import { ArrowRight, MapPin } from "lucide-react";

export default function TeamPage() {
  const { league, team } = useParams();
  const { data, loading, error } = useFetch(`/team/${team}`, [team]);

  if (loading) return <Loading />;
  if (error || !data?.team) return <DetailError error={error} path={`/${league}/${team}`} notFound={!error} />;
  const { team: t, venue, league: lg, city, events } = data;

  return (
    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
      <Seo title={`${t.name} — Where to Stay for Home Games | Hotelstrips`}
        description={`Travel guide for ${t.name} home games at ${venue?.name}. Best areas to stay, transit and hotels.`}
        path={`/${league}/${team}`} />
      <Breadcrumbs items={[{ label: "GameDay", to: "/gameday" }, { label: lg?.name, to: `/${league}` }, { label: t.name }]} />

      <div className="grid gap-8 lg:grid-cols-2 items-center mb-12">
        <div>
          <div className="overline text-primary mb-2">{lg?.name} • {city?.name}</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">{t.name}</h1>
          <p className="text-muted-foreground mt-3">{t.description}</p>
          {venue && (
            <Link to={`/${league}/${team}/${venue.slug}`} data-testid="team-venue-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-heading font-bold text-primary-foreground transition-transform hover:-translate-y-0.5">
              Where to stay for {venue.name} <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
        {venue && (
          <Link to={`/${league}/${team}/${venue.slug}`} className="group relative block overflow-hidden rounded-xl aspect-[16/10]">
            <img src={venue.image_url} alt={venue.image_alt || venue.name} onError={onImgError} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 img-overlay" />
            <div className="absolute bottom-0 p-6">
              <div className="overline text-white/70 flex items-center gap-1.5 mb-1"><MapPin className="h-3 w-3" />Home venue</div>
              <h2 className="font-heading text-2xl font-bold text-white">{venue.name}</h2>
            </div>
          </Link>
        )}
      </div>

      {events?.length > 0 && (
        <div className="mb-12">
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-ink mb-5">Upcoming home games</h2>
          <div className="grid gap-4 md:grid-cols-2">{events.map((e) => <EventCard key={e.slug} event={e} />)}</div>
        </div>
      )}

      <div className="rounded-2xl bg-ink text-white p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
        <div>
          <h3 className="font-heading text-2xl font-bold tracking-tight">Plan the whole trip</h3>
          <p className="text-white/70 mt-1">Hotels, flights and transfers for {t.name} gameday.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <CTAButton productType="hotels" ctx={{ page: "team", venue: venue?.slug, city: city?.slug, position: "team_band" }} />
          <CTAButton productType="flights" variant="outline" className="!text-white !border-white/30" ctx={{ page: "team", city: city?.slug, position: "team_band" }} />
        </div>
      </div>
    </div>
  );
}
